import { useParams, useRouterState } from '@tanstack/react-router'
import { useSessionUser } from '@/hooks/use-session'
import { useLingui } from '@lingui/react/macro'
import { cn } from '@/lib/utils'
import { useNavVisualPath } from '@/components/custom/route-transition/core/nav-visual-path'
import { matchesFrom } from '@/components/custom/route-transition/surface/surface.config'
import { TransitionLink } from '@/components/custom/route-transition/surface/transition-link'
import { useActiveEmployer } from '@/hooks/use-active-employer'
import { usePermissions } from '@/hooks/use-permissions'
import { clearProfileSelection } from '@/hooks/use-root-search'
import { NAVIGATION_ICONS, type NavigationIcon } from './shared/navigation-config'
import { SectionIcon } from './shared/section-icon'

// The viewer's top-level SECTION links, rendered once and placed twice: as a row inside the topbar on
// desktop (`variant="topbar"`) and as the sticky bottom bar on mobile (`variant="bottom"`, see
// app-bottom-nav.tsx). Only one of the two is ever in the layout — each placement carries the matching
// `hidden`/`md:hidden`, which also keeps it out of the a11y tree (and out of Playwright's role queries).
//
// The gates below must stay in lockstep with useTopLevelSections() (the swipe roster for the section
// pager), in the SAME order — see the header comment there.
//
// The ACTIVE highlight is driven by the route-transition visual path (navVisualPath), NOT by aria-current, so
// that a SWIPE can carry it: mid-drag it tracks the pane under the finger and flips at the midpoint. A CLICK is
// the opposite case and lights its destination on the frame you tap it, like any ordinary link — see the engine
// (scroll-transition-outlet) for which of the two sets it. The <TransitionLink> still sets aria-current for
// accessibility, and is also what fires the tap haptic.

export type SectionNavVariant = 'topbar' | 'bottom'

// `touch-action: manipulation` is the tap-latency lever: it opts the link out of double-tap-to-zoom, so mobile
// engines don't hold the click back waiting to see whether a second tap is coming. The glide starts in the click
// handler, so anything the platform adds before `click` is pure dead time.
const NAV_LINK_BASE =
  'text-muted-foreground hover:bg-muted/50 hover:text-foreground flex touch-manipulation items-center justify-center rounded-lg text-sm'
const NAV_LINK_ACTIVE = 'bg-muted/70 text-foreground'
// Topbar: label-only pills in a horizontal row. Bottom bar: icon-over-label cells that split the full
// width evenly (min-w-0 so a long label truncates instead of widening its cell).
const NAV_LINK_VARIANT: Record<SectionNavVariant, string> = {
  topbar: 'flex-row gap-2.5 px-4 py-2.5',
  bottom: 'min-w-0 flex-1 flex-col gap-0.5 px-1 py-1.5',
}
const NAV_LABEL_VARIANT: Record<SectionNavVariant, string> = {
  topbar: 'text-sm leading-normal font-medium',
  bottom: 'max-w-full truncate text-center text-[10px] leading-tight font-medium',
}
const NAV_VARIANT: Record<SectionNavVariant, string> = {
  topbar: 'text-muted-foreground hidden flex-1 items-center gap-1 font-medium md:flex',
  bottom: 'text-muted-foreground flex items-stretch gap-0.5 p-1 font-medium',
}

/** Icon (bottom bar only — the topbar row is label-only) + label, shared by every link below. */
function NavItem(props: { icon: NavigationIcon; label: string; variant: SectionNavVariant }) {
  return (
    <>
      {props.variant === 'bottom' && <SectionIcon icon={props.icon} className="size-5 shrink-0" />}
      <span className={NAV_LABEL_VARIANT[props.variant]}>{props.label}</span>
    </>
  )
}

export function AppSectionNav({ variant }: { variant: SectionNavVariant }) {
  const { t } = useLingui()
  const user = useSessionUser()
  const { orgId, groupId } = useParams({ strict: false })
  // The RENDERED pathname (the deepest committed match) as the RESTING state, overridden by navVisualPath
  // while a transition owns the highlight. Not `location.pathname`: the router flips that the instant a link is
  // clicked, which used to leave three highlight states in one tap — destination, then back to the origin once
  // the glide claimed it, then destination again at the midpoint. Reading the matches means the resting value
  // only ever moves when the pane you're looking at does, and the transition is the single thing that moves it
  // early. Same signal the pager takes its geometry from (route-transition/scroll-transition-outlet).
  const realPath = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.pathname ?? s.location.pathname,
  })
  const visualPath = useNavVisualPath() ?? realPath
  // Active when the (transition-aware) visual path is on this section — exact or any nested child.
  const navCls = (section: string) =>
    cn(NAV_LINK_BASE, NAV_LINK_VARIANT[variant], matchesFrom(visualPath, section) && NAV_LINK_ACTIVE)

  const isApplicant = user?.type === 'applicant'
  // Professional who self-registered (not invited/registered by a company)
  const isSelfApplicant = isApplicant && !user?.isEmployee
  // Professional registered as an employee by a company
  const isEmployee = isApplicant && user?.isEmployee
  const { isCompanyView, can } = usePermissions()
  const canAccessOrgs = can('orgs:read')

  // Global employer context: an employee's report/views are scoped to the active company. Switching
  // context lives in the user menu (UserMenu).
  const { activeEmployerId } = useActiveEmployer()

  // Section nav is for signed-in users only. Anonymous visitors (who only ever reach the public
  // report/market pages) get just the logo + the "compare my profile" CTA in the topbar — none of these
  // destinations are reachable logged out, so showing them would only dead-end at the login wall.
  if (!user) return null

  return (
    <nav className={NAV_VARIANT[variant]}>
      {/* Organizations - Viventia specific (static identity capability, see canAccessOrgs) */}
      {canAccessOrgs && (
        <TransitionLink
          to={orgId && groupId ? '/orgs/$orgId/groups/$groupId/report' : '/orgs'}
          params={orgId && groupId ? { orgId, groupId } : undefined}
          search={(prev) => (prev.first?.type === 'org_group' ? { ...prev, q: undefined } : {})}
          aria-label={t`Organizations`}
          className={navCls('/orgs')}
        >
          <NavItem icon={NAVIGATION_ICONS.organizations} label={t`Organizations`} variant={variant} />
        </TransitionLink>
      )}

      {/* Employees - For companies (and a promoted admin/authorized employee in company view) */}
      {isCompanyView && (
        <TransitionLink
          to="/employees"
          search={{ q: undefined }}
          aria-label={t`Employees`}
          className={navCls('/employees')}
        >
          <NavItem icon={NAVIGATION_ICONS.employees} label={t`Employees`} variant={variant} />
        </TransitionLink>
      )}

      {/* My report - For applicants (hidden when an employee is viewing the company interface) */}
      {isApplicant && !isCompanyView && (
        <TransitionLink
          to="/report"
          aria-label={isSelfApplicant || isEmployee ? t`My profile` : t`My report`}
          className={navCls('/report')}
        >
          <NavItem
            icon={NAVIGATION_ICONS.report}
            label={isSelfApplicant || isEmployee ? t`My profile` : t`My report`}
            variant={variant}
          />
        </TransitionLink>
      )}

      {/* Puesto (assigned position) — employer-specific, so only when a company is the active
          context (hidden on the personal D'Anchiano profile, and in the company view). */}
      {isEmployee && activeEmployerId !== null && !isCompanyView && (
        <TransitionLink
          to="/jobs"
          search={{
            q: undefined,
            first: undefined,
            second: undefined,
            selected: undefined,
            type: undefined,
            o: undefined,
            od: undefined,
          }}
          aria-label={t`Position`}
          className={navCls('/jobs')}
        >
          <NavItem icon={NAVIGATION_ICONS.jobs} label={t`Position`} variant={variant} />
        </TransitionLink>
      )}

      {/* Jobs - For companies (and a promoted admin/authorized employee in company view) */}
      {isCompanyView && (
        <TransitionLink
          to="/jobs"
          search={{
            q: undefined,
            first: undefined,
            second: undefined,
            selected: undefined,
            type: undefined,
            o: undefined,
            od: undefined,
          }}
          aria-label={t`Jobs`}
          className={navCls('/jobs')}
        >
          <NavItem icon={NAVIGATION_ICONS.jobs} label={t`Jobs`} variant={variant} />
        </TransitionLink>
      )}

      {/* Teams — hidden for self-registered professionals. For employees it's employer-specific,
          so only when a company is the active context; companies always see it. */}
      {!isSelfApplicant && (!isEmployee || activeEmployerId !== null) && (
        <TransitionLink to="/teams" search={{ q: undefined }} aria-label={t`Teams`} className={navCls('/teams')}>
          <NavItem icon={NAVIGATION_ICONS.teams} label={t`Teams`} variant={variant} />
        </TransitionLink>
      )}

      {/* Offers - companies + a promoted admin/authorized employee in company view (not self-pros,
          not employees in their own employee view) */}
      {!isSelfApplicant && (!isEmployee || isCompanyView) && (
        <TransitionLink
          to="/offers"
          search={{
            q: undefined,
            first: undefined,
            second: undefined,
            selected: undefined,
            type: undefined,
            o: undefined,
            od: undefined,
          }}
          aria-label={t`Offers`}
          className={navCls('/offers')}
        >
          <NavItem icon={NAVIGATION_ICONS.offers} label={t`Offers`} variant={variant} />
        </TransitionLink>
      )}

      {/* Market - For companies (and a promoted admin/authorized employee in company view) */}
      {isCompanyView && (
        <TransitionLink
          to="/market"
          search={(prev) => ({ ...clearProfileSelection(prev), q: undefined, first: { type: 'market' } })}
          activeOptions={{ includeSearch: false }}
          aria-label={t`Market`}
          className={navCls('/market')}
        >
          <NavItem icon={NAVIGATION_ICONS.market} label={t`Market`} variant={variant} />
        </TransitionLink>
      )}
    </nav>
  )
}
